import type { ReplayRecorder } from './recorder';
import { NetworkEventPayload, redactText } from './redaction';

/** Kayıt başlamadan önce (config + rrweb yüklenirken) atılan istekler; başlarken kayda eklenir. */
const PRE_START_MAX = 100;

/**
 * Uygulama geneli tekil erişim noktası. Provider kaydediciyi buraya bağlar;
 * `api/config.ts` (ağ), ErrorBoundary (hata) ve router (nav) buradan olay basar.
 * Kaydedici yokken tüm çağrılar sessizce yutulur.
 */
/** Özet mod: gövdeler düşer, yalnız metot/URL/durum/süre/başlık özeti kalır (~200 bayt). */
export const stripBodies = (ev: NetworkEventPayload): NetworkEventPayload => ({
  ...ev,
  reqBody: undefined,
  resBody: undefined,
  reqTruncated: undefined,
  resTruncated: undefined,
  bodiesOmitted: true
});

class ReplayClient {
  private recorder: ReplayRecorder | null = null;
  private preStart: NetworkEventPayload[] = [];

  attach(recorder: ReplayRecorder | null) {
    this.recorder = recorder;
    if (recorder && this.preStart.length) {
      // Sayfa ilk açılış istekleri (en değerlileri) kaybolmasın
      const queued = this.preStart;
      this.preStart = [];
      queued.forEach((ev) => recorder.custom('network', { ...(recorder.captureBodies ? ev : stripBodies(ev)), preStart: true }));
    }
  }

  /** İstek/yanıt gövdeleri şu an kaydediliyor mu (kullanıcı izni veya canlı izleyici isteği). */
  get bodiesEnabled(): boolean {
    return Boolean(this.recorder?.captureBodies);
  }

  /** Kaydedici henüz yokken gelen (redakte edilmiş) ağ olayını kuyruğa al. */
  queueNetwork(ev: NetworkEventPayload) {
    if (this.preStart.length >= PRE_START_MAX) this.preStart.shift();
    this.preStart.push(ev);
  }

  get isRecording(): boolean {
    return Boolean(this.recorder?.isRecording);
  }

  get sessionId(): string | undefined {
    return this.recorder?.sessionId;
  }

  custom<T>(tag: string, payload: T): void {
    this.recorder?.custom(tag, payload);
  }

  /** JS hatası / React hata sınırı */
  captureError(error: unknown, extra?: { componentStack?: string | null; source?: string }): void {
    if (!this.recorder?.isRecording) return;
    const err = error instanceof Error ? error : new Error(String(error));
    this.recorder.custom('error', {
      message: redactText(err.message ?? ''),
      name: err.name,
      stack: err.stack ? redactText(err.stack).slice(0, 8000) : undefined,
      componentStack: extra?.componentStack ? redactText(extra.componentStack).slice(0, 8000) : undefined,
      source: extra?.source ?? 'manual',
      url: location.pathname
    });
  }
}

export const replayClient = new ReplayClient();
